import {
  CustomFieldExtensionSchema,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import {
  FormControlLabel,
  FormLabel,
  Link,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';

import {
  configApiRef,
  IdentityApi,
  identityApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { WorkloadClient } from '@internal/workload-api-helper';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import { FieldValidation } from '@rjsf/utils';
import { useForm } from 'react-hook-form';
import { validateField } from '../helper/helper';

export const ExistingRepositoryExtensionSchema: CustomFieldExtensionSchema = {
  returnValue: { type: 'string' },
};

export const ExistingRepository = ({
  onChange,
  rawErrors,
  required,
  formData,
}: FieldExtensionComponentProps<string>) => {
  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors !== undefined && rawErrors.length > 0 && !formData}
    >
      <FormLabel id="existing-repository-radio-buttons-group">
        Do you already have an existing GitHub repository for this workload?
      </FormLabel>
      <RadioGroup
        aria-labelledby="existing-repository-radio-buttons-group"
        name="existing-repository-radio-buttons-group"
        value={formData}
        onChange={e => onChange(e.target?.value)}
        row
      >
        <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
        <FormControlLabel value="No" control={<Radio />} label="No" />
      </RadioGroup>
    </FormControl>
  );
};

export const OIDCRoleExtensionSchema: CustomFieldExtensionSchema = {
  returnValue: { type: 'string' },
};

export const OIDCRole = ({
  onChange,
  rawErrors,
  required,
  formData,
}: FieldExtensionComponentProps<string>) => {
  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors !== undefined && rawErrors.length > 0 && !formData}
    >
      <FormLabel id="oidc-role-radio-buttons-group">
        Would you like us to create a GitHub Actions OIDC role tied to the
        GitHub repository for you?
      </FormLabel>
      <RadioGroup
        aria-labelledby="oidc-role-radio-buttons-group"
        name="oidc-role-radio-buttons-group"
        value={formData}
        onChange={e => onChange(e.target?.value)}
        row
      >
        <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
        <FormControlLabel value="No" control={<Radio />} label="No" />
      </RadioGroup>
      <FormHelperText id="oidcRoleHelperText">
        <Typography variant="body1">
          See the{' '}
          <Link
            target="_blank"
            href="https://portal.developer.principal.com/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/infrastructure/aws/what-comes-with-my-aws-account"
          >
            docs
          </Link>{' '}
          for more details. Typically used in conjunction with the CDK Bootstrap
          option.
        </Typography>
      </FormHelperText>
    </FormControl>
  );
};

export const CreateRepositoryExtensionSchema: CustomFieldExtensionSchema = {
  returnValue: { type: 'string' },
};

export const CreateRepository = ({
  onChange,
  rawErrors,
  required,
  formData,
}: FieldExtensionComponentProps<string>) => {
  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors !== undefined && rawErrors.length > 0 && !formData}
    >
      <FormLabel id="create-repository-radio-buttons-group">
        Would you like us to create a GitHub repository for you?
      </FormLabel>
      <RadioGroup
        aria-labelledby="create-repository-radio-buttons-group"
        name="create-repository-radio-buttons-group"
        value={formData}
        onChange={e => onChange(e.target?.value)}
        row
      >
        <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
        <FormControlLabel value="No" control={<Radio />} label="No" />
      </RadioGroup>
      <FormHelperText id="createRepositoryHelperText">
        <Typography variant="body1">
          See the{' '}
          <Link
            target="_blank"
            href="https://portal.developer.principal.com/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/design/platform-selection/aws/what-comes-with-my-aws-account/#github-repository"
          >
            docs
          </Link>{' '}
          for more details.
        </Typography>
      </FormHelperText>
    </FormControl>
  );
};

export const RepositoryNameExtensionSchema: CustomFieldExtensionSchema = {
  returnValue: { type: 'string' },
};

let config: any;
let identity: IdentityApi;
let formContext: any;
let workloadClient: WorkloadClient;

export const RepositoryName = ({
  onChange,
  rawErrors,
  required,
  formData,
}: FieldExtensionComponentProps<string>) => {
  // Set up variables for accounts-api validation call
  config = useApi(configApiRef);
  identity = useApi(identityApiRef);
  const oauthApi = useApi(microsoftAuthApiRef);
  workloadClient = new WorkloadClient(config, oauthApi);

  formContext = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    shouldUnregister: true,
    defaultValues: {
      existing_repository: 'No',
      vpc_required: 'No',
      public_subnet: 'No',
      repository_creation_enabled: 'No',
      pfg_owner: '',
      governance_group_membership: [
        {
          value: '',
        },
        {
          value: '',
        },
      ],
    },
  });

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors !== undefined && rawErrors.length > 0 && !formData}
    >
      <InputLabel htmlFor="repositoryNameInput">
        GitHub organization/repository name
      </InputLabel>
      <Input
        id="repositoryNameInput"
        defaultValue={formData}
        aria-describedby="repositoryNameHelperText"
        onChange={e => onChange(e.target?.value)}
      />
      <FormHelperText id="repositoryNameHelperText">
        Example:
        <code>
          principalinformationservices-emu/aws-workload-threepet-infra
        </code>
      </FormHelperText>
    </FormControl>
  );
};

export const validateOIDCRole = (
  value: string,
  validation: FieldValidation,
  { formData }: any,
) => {
  if (
    formData.orgUnit.includes('Principal Asset Management') &&
    value === 'Yes'
  ) {
    validation.addError(
      'You cannot create an OIDC role for a Principal Asset Management workload',
    );
    return;
  }
  if (formData.existingRepository === 'No' && value === 'Yes') {
    validation.addError(
      'You must have an existing repository in order to select "Yes"',
    );
  }
};

export const validateCreateRepository = (
  value: string,
  validation: FieldValidation,
  { formData }: any,
) => {
  if (
    formData.orgUnit.includes('Principal Asset Management') &&
    value === 'Yes'
  ) {
    validation.addError(
      'You cannot create a repository for a Principal Asset Management workload',
    );
    return;
  }
  if (formData.existingRepository === 'Yes' && value === 'Yes') {
    validation.addError(
      'You cannot select "Yes" to have a repository created if you have an existing repository.',
    );
  }
};

export const validateRepositoryName = async (
  value: string,
  validation: FieldValidation,
  { formData }: any,
): Promise<void> => {
  formContext.setValue(
    'repository_creation_enabled',
    formData.createRepository,
  );
  formContext.setValue('existing_repository', formData.existingRepository);
  formContext.setValue('pfg_owner', formData.accountOwner);

  const apiValidation = {
    form: formContext,
    config: config,
    identity: identity,
    workloadClient: workloadClient,
  };

  if (
    !value &&
    formData.existingRepository === 'Yes' &&
    formData.createRepository === 'No'
  ) {
    validation.addError(
      'Please enter a name for the repository you would like to be created.',
    );
  } else if (
    !value &&
    formData.existingRepository === 'No' &&
    formData.createRepository === 'Yes'
  ) {
    validation.addError('Please enter your existing repository name.');
  } else if (
    value &&
    formData.createRepository === 'No' &&
    formData.existingRepository !== 'Yes'
  ) {
    validation.addError(
      'You have not indicated that you have an existing repo or want a new repo. Please clear the field before proceeding.',
    );
  } else if (
    value &&
    formData.existingRepository === 'No' &&
    formData.createRepository === 'Yes'
  ) {
    const errorMessage = await validateField(
      'repository',
      value,
      apiValidation,
    );
    if (errorMessage) validation.addError(errorMessage);
  } else if (
    value &&
    formData.existingRepository === 'Yes' &&
    formData.createRepository === 'No'
  ) {
    const errorMessage = await validateField(
      'repository',
      value,
      apiValidation,
    );
    if (errorMessage) validation.addError(errorMessage);
  } else if (!value) {
    return;
  }
};
